// src/pages/Admin/EditUserAdmin.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import useAccess from "../../hooks/useAccess";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "../../styles/Form.css";
import LoadingOverlay from "../../components/LoadingOverlay";
import AddressSection from "../../components/AddressSection";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import { toDateOnly } from "../../utils/dateOnly";
import { formatPhoneNumber, phoneDigits } from "../../utils/phone";
import PermissionNotice from "../../components/PermissionNotice";
import { notifyPermissionDenied } from "../../components/PermissionFeedback";

const PERSONAL_INFORMATION_TASK_ID = 18;

function EditUserAdmin() {
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdminRole = userRole === "1";
  const userId = isAdminRole && paramId ? paramId : loggedInId;
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminUserManagement =
    new URLSearchParams(location.search).get("source") === "admin-users";

  const loggedInUserRoles = JSON.parse(
    localStorage.getItem("emp_roles") || "[]",
  );
  const loggedInUserIsSuperAdmin = loggedInUserRoles.includes(17);

  const [form, setForm] = useState({
    role: "",
    refer_by_name: "",
    refer_by_id: "",
  });

  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [profilePic, setProfilePic] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generating, setGenerating] = useState(false);

  const [status, setStatus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [positions, setPositions] = useState([]);
  const [positionTypes, setPositionTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [empRoles, setEmpRoles] = useState([]);
  const [empGroups, setEmpGroups] = useState([]);
  const [empOrgs, setEmpOrgs] = useState([]);
  const [canGroups, setCanGroups] = useState([]);
  const [hearAboutList, setHearAboutList] = useState([]);
  const [recruiterList, setRecruiterList] = useState([]);
  const [showSin, setShowSin] = useState(false);

  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { hasPermission, permissionsLoaded } = useAccess();

  // Permission check for Admin User page
  const canViewAdminUsers =
    hasPermission("AdminUser", "User", "view") ||
    hasPermission("AdminUser", "User", "edit");
  const canAssign = hasPermission("AdminUser", "User", "edit");
  const canEditProfile =
    hasPermission("AdminUser", "User", "edit") ||
    hasPermission("AdminDashboard", "User", "edit");
  const canViewSin = hasPermission("Task", "SIN", "view");
  const canEditSin = hasPermission("Task", "SIN", "edit");

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const isAdmin = Number(form.role) === 1;
  const isCandidate = Number(form.status) === 3;
  const safeParseArray = (json) => {
    try {
      const parsed = JSON.parse(json || "[]");
      return Array.isArray(parsed) ? parsed.map(Number) : [];
    } catch {
      return [];
    }
  };

  const parseReferBy = (rb) => {
    if (!rb) return { name: "", employeeId: "" };
    try {
      const obj = typeof rb === "string" ? JSON.parse(rb) : rb;
      return {
        name: obj?.name || "",
        employeeId: obj?.employeeId || "",
      };
    } catch {
      return { name: String(rb), employeeId: "" };
    }
  };

  const maskSin = (sin, show) => {
    if (!sin) return "";
    if (show) return sin;
    return `*****${sin.slice(-4)}`;
  };

  // Load user + static dropdowns
  useEffect(() => {
    const token = localStorage.getItem("token");

    const fetchUser = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/users/${paramId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const parsedRoles = Array.isArray(res.data.emp_roles)
          ? res.data.emp_roles.map(Number)
          : safeParseArray(res.data.emp_roles);

        const parsedGroups = Array.isArray(res.data.emp_groups)
          ? res.data.emp_groups.map(Number)
          : safeParseArray(res.data.emp_groups);

        const parsedOrgs = Array.isArray(res.data.emp_orgs)
          ? res.data.emp_orgs.map(Number)
          : safeParseArray(res.data.emp_orgs);

        const { name: refer_by_name, employeeId: refer_by_id } = parseReferBy(
          res.data.refer_by,
        );

        setForm({
          ...res.data,
          role: String(res.data.role),
          emp_roles: parsedRoles,
          emp_groups: parsedGroups,
          emp_orgs: parsedOrgs,
          dob: res.data.role === 1 ? "" : toDateOnly(res.data.dob),
          refer_by_name,
          refer_by_id,
        });

        // 🔐 ensure password input starts blank (don’t show hashed value)
        setForm((prev) => ({ ...prev, password: "" }));

        setSelectedRoles(parsedRoles);
        setSelectedGroups(parsedGroups);
        setSelectedOrgs(parsedOrgs);
        setConfirmPassword("");

        if (res.data.profilePic) {
          setPreviewUrl(
            `${BASE_URL}/uploads/profilepic/${res.data.profilePic}`,
          );
        }
      } catch (err) {
        console.error("Failed to fetch user:", err);
      }
    };

    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/dropdowns/user-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStatus(res.data);
      } catch (err) {
        console.error("Failed to fetch status", err);
      }
    };

    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/dropdowns/categories`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCategories(res.data);
      } catch (err) {
        console.error("Failed to fetch categories", err);
      }
    };

    const fetchOrgUnit = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/dropdowns/org-units`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrgUnits(res.data);
        setEmpOrgs(res.data);
      } catch (err) {
        console.error("Failed to fetch Org Units", err);
      }
    };

    const fetchPositionType = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/dropdowns/position-types`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setPositionTypes(res.data);
      } catch (err) {
        console.error("Failed to fetch Position Types", err);
      }
    };

    const fetchCountries = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/dropdowns/countries`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCountries(res.data);
      } catch (err) {
        console.error("Failed to fetch countries data", err);
      }
    };

    fetchUser();
    fetchStatus();
    fetchCategories();
    fetchOrgUnit();
    fetchPositionType();
    fetchCountries();
  }, [paramId, BASE_URL]);

  // Positions (depends on emp_category)
  const empCategory = form?.emp_category ?? ""; // 👈 derive outside

  useEffect(() => {
    const token = localStorage.getItem("token");
    const fetchPositions = async () => {
      try {
        const url = empCategory
          ? `${BASE_URL}/api/positions/by-category/${empCategory}`
          : `${BASE_URL}/api/positions`;
        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPositions(res.data);
      } catch (err) {
        console.error("Failed to fetch positions:", err);
      }
    };
    fetchPositions();
  }, [BASE_URL, empCategory]); // 👈 depends on the primitive, not form

  // Roles (only for Admin) + Groups (for everyone)
  useEffect(() => {
    const token = localStorage.getItem("token");

    const fetchRoles = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/dropdowns/emp-role`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEmpRoles(res.data);
      } catch (err) {
        console.error("Error fetching roles", err);
      }
    };

    const fetchGroups = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/dropdowns/position-groups`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setEmpGroups(res.data);
        setCanGroups(res.data);
      } catch (err) {
        console.error("Error fetching groups", err);
      }
    };

    if (isAdmin) fetchRoles();
    fetchGroups();
  }, [BASE_URL, isAdmin]);

  // Hear About Us + Recruiters (Candidate fields)
  useEffect(() => {
    const token = localStorage.getItem("token");
    const fetchHearAbout = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/dropdowns/hear-about-us`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setHearAboutList(res.data);
      } catch (err) {
        console.error("Failed to fetch Hear About Us list", err);
      }
    };
    const fetchRecruiters = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/dropdowns/recruiters`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRecruiterList(res.data); // [{label, value}]
      } catch (err) {
        console.error("Failed to fetch Recruiter list", err);
      }
    };
    fetchHearAbout();
    fetchRecruiters();
  }, [BASE_URL]);

  // 🚫 Rule:
  // If user is NOT allowed to view admin users page,
  // AND they are trying to open someone ELSE’s account → BLOCK THEM.
  if (!permissionsLoaded && String(paramId) !== String(loggedInId)) {
    return (
      <div className="container py-5 text-center">
        <p>Checking access...</p>
      </div>
    );
  }

  if (!canViewAdminUsers && String(paramId) !== String(loggedInId)) {
    return (
      <div className="container py-5 text-center">
        <h4 className="text-danger">Access Denied</h4>
        <p>You do not have permission to view this user.</p>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = ["mobile", "home_phone"].includes(name)
      ? phoneDigits(value)
      : value;
    setForm((prev) => ({ ...prev, [name]: nextValue }));
  };

  const toggleSelection = (id, list, setList) => {
    setList((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleGenerateId = async () => {
    try {
      setGenerating(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${BASE_URL}/api/employee-number/next`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm((prev) => ({ ...prev, userid_no: res.data.employeeNumber }));
    } catch (err) {
      console.error("Failed to get next employee number", err);
      alert("Failed to get next Employee ID");
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEditProfile) {
      notifyPermissionDenied("You don't have permission to edit user profiles.");
      return;
    }
    if (form.password && form.password !== confirmPassword) {
      alert("Passwords do not match!");
      return;
    }
    try {
      setIsSubmitting(true); // ✅ start loading

      const token = localStorage.getItem("token");
      const formData = new FormData();

      const selectedPosition = positions.find(
        (p) => String(p.p_id) === String(form.position_full), // now using p_id
      );
      const positionTitle = selectedPosition?.p_position_title || "";
      const derivedOrgUnit = selectedPosition?.p_org_unit || "";

      const payload = {
        ...form,
        position: positionTitle,
        org_unit: derivedOrgUnit, // <-- derived
        emp_roles: JSON.stringify(selectedRoles),
        emp_groups: JSON.stringify(selectedGroups),
        emp_orgs: JSON.stringify(selectedOrgs),
        refer_by:
          form.refer_status === "Yes"
            ? JSON.stringify({
                name: (form.refer_by_name || "").trim(),
                employeeId: (form.refer_by_id || "").trim(),
              })
            : "",
      };
      // remove helper fields
      delete payload.refer_by_name;
      delete payload.refer_by_id;

      // mirror UserForm: if Admin, force blank dob on submit
      if (String(payload.role) === "1") {
        payload.dob = "";
      }

      Object.entries(payload).forEach(([key, value]) => {
        if (key === "password" && !form.password) return; // keep existing if blank
        formData.append(key, value ?? "");
      });

      if (profilePic) formData.append("profilePic", profilePic);

      // ✅ Add explicit userId so uploader saves under correct folder
      formData.append("userId", paramId);

      await axios.put(
        `${BASE_URL}/api/users/${paramId}?userId=${paramId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setShowSin(false);
      alert("User updated!");
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Error updating user.";
      alert(msg);
    } finally {
      setIsSubmitting(false); // ✅ stop loading
    }
  };


  const positionOptions = positions.map((pos) => {
    const orgName =
      orgUnits.find((o) => o.ou_id === parseInt(pos.p_org_unit))?.ou_name ||
      "Unknown Unit";
    const typeName =
      positionTypes.find((pt) => pt.pt_id === parseInt(pos.p_position_type))
        ?.pt_name || "Unknown Type";
    const city = pos.p_contact_info?.city || "";
    const openings = pos.p_posting?.p_num_openings ?? "N/A";
    return {
      value: pos.p_id, // ⬅️ use primary key
      title: pos.p_position_title,
      orgName,
      typeName,
      displayLabel: `${pos.p_position_title} - ${orgName} - ${typeName} - ${city}`,
      subtext: `Pos No: ${pos.p_position_number} - No. Openings: ${openings}`,
    };
  });

  if (!form) return <div className="container mt-5">Loading...</div>;

  return (
    <>
      {isSubmitting && <LoadingOverlay text="Saving changes..." />}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Pg. 1 - Personal Information
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(-1)}
        >
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-4 text-start" />
          <div className="col-4 text-center">
            <h3 className="mb-0">Pg. 1 - Personal Information</h3>
          </div>
        </div>

        {permissionsLoaded && !canEditProfile && (
          <PermissionNotice resource="user profiles" />
        )}

        <form onSubmit={handleSubmit}>
          <fieldset
            className="permission-readonly-fieldset"
            disabled={!permissionsLoaded || !canEditProfile}
          >
          {/* Identifiers */}
          <fieldset className="border p-3 mb-4">
            <legend className="w-auto px-2">User Identifiers</legend>
            <div className="row mb-3">
              {!isCandidate && !isAdmin && (
                <>
                  {/* Employee ID (Associate only or when already exists) */}
                  <div className="col-md-3">
                    <label>
                      Employee ID <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        name="userid_no"
                        className="form-control"
                        value={form.userid_no}
                        onChange={handleChange}
                        required
                      />
                      {!isAdmin && !form.userid_no && (
                        <button
                          type="button"
                          onClick={handleGenerateId}
                          className="btn btn-secondary"
                          disabled={generating}
                          title="Get next available Employee ID"
                        >
                          {generating ? "Generating..." : "Generate ID"}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
              {/* <div className="col-md-3 mb-3">
                <label>Role</label>
                <select
                  name="role"
                  className="form-select"
                  value={form.role ?? ""}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select</option>
                  <option value="2">User</option>
                  <option value="1">Admin</option>
                </select>
              </div> */}
              <div className="col-md-3">
                <label>Role</label>
                <input
                  type="text"
                  name="role"
                  className="form-control"
                  value={
                    form.role === "1"
                      ? "Admin"
                      : form.role === "2"
                        ? "User"
                        : ""
                  }
                  disabled
                />
              </div>

              {/* <div className="col-md-3">
                <label>Status</label>
                <select
                  name="status"
                  className="form-select"
                  value={form.status ?? ""}
                  onChange={handleChange}
                  disabled
                >
                  <option value="">Select</option>
                  {status
                    .filter((s) => {
                      const role = Number(form.role);
                      const statusId = Number(s.s_id);
                      if (statusId === 1) return true; // Always show Associate (1)
                      if (role === 1) return false; // Admin → only Associate
                      return statusId === 3; // User/empty → Candidate (3)
                    })
                    .map((s) => (
                      <option key={s.s_id} value={s.s_id}>
                        {s.s_TranslatedText}
                      </option>
                    ))}
                </select>
              </div> */}

              <div className="col-md-3">
                <label>Status</label>
                <input
                  type="text"
                  name="status"
                  className="form-control"
                  value={
                    status.find((s) => String(s.s_id) === String(form.status))
                      ?.s_TranslatedText ?? ""
                  }
                  disabled
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="border p-3 mb-4">
            <legend className="w-auto px-2">Name Details</legend>
            <div className="row mb-3">
              <div className="col-md-3">
                <label>
                  First Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  className="form-control"
                  value={form.first_name ?? ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="col-md-3">
                <label>Middle Name</label>
                <input
                  type="text"
                  name="mid_name"
                  className="form-control"
                  value={form.mid_name ?? ""}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-3">
                <label>
                  Last Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  className="form-control"
                  value={form.last_name ?? ""}
                  onChange={handleChange}
                  required
                />
              </div>
              {!isAdmin && (
                <div className="col-md-1">
                  <label>Suffix</label>
                  <input
                    type="text"
                    name="suffix"
                    className="form-control"
                    value={form.suffix ?? ""}
                    onChange={handleChange}
                  />
                </div>
              )}
              {!isAdmin && (
                <div className="col-md-2">
                  <label>Gender</label>
                  <select
                    name="gender"
                    className="form-select"
                    value={form.gender ?? ""}
                    onChange={handleChange}
                  >
                    <option value="">-</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              )}
            </div>
          </fieldset>

          {!isAdmin && (
            <fieldset className="border p-3 mb-4">
              <legend className="w-auto px-2">Contact & Identity</legend>
              <div className="row gy-3 mb-3">
                <div className="col-md-3">
                  <label>Preferred Name</label>
                  <input
                    type="text"
                    name="pre_name"
                    className="form-control"
                    value={form.pre_name}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-md-3">
                  <label>
                    Date of Birth <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    name="dob"
                    className="form-control"
                    value={form.dob ?? ""}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="col-md-3">
                  <label>
                    Mobile <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    placeholder="e.g. (403) 123-4567"
                      pattern="[(][0-9]{3}[)] [0-9]{3}-[0-9]{4}"
                    title="Please enter a 10-digit phone number"
                    className="form-control"
                    value={formatPhoneNumber(form.mobile)}
                    maxLength={14}
                    inputMode="numeric"
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-md-3">
                  <label>Home Phone</label>
                  <input
                    type="tel"
                    name="home_phone"
                    placeholder="e.g. (403) 123-4567"
                      pattern="[(][0-9]{3}[)] [0-9]{3}-[0-9]{4}"
                    title="Please enter a 10-digit phone number"
                    className="form-control"
                    value={formatPhoneNumber(form.home_phone)}
                    maxLength={14}
                    inputMode="numeric"
                    onChange={handleChange}
                  />
                </div>
                {canViewSin && <div className="col-md-3">
                  <label>S.I.N.</label>
                  <div className="input-group">
                    <input
                      type="text"
                      name="sin"
                      className="form-control"
                      value={
                        showSin ? form.sin || "" : maskSin(form.sin, false)
                      }
                      onFocus={() => setShowSin(true)}
                      onChange={(e) => {
                        setShowSin(true);
                        const nextSin = e.target.value.replace(/\D/g, "");
                        setForm((prev) => ({ ...prev, sin: nextSin }));
                      }}
                      inputMode="numeric"
                      maxLength={9}
                      placeholder="Enter SIN"
                      disabled={!canEditSin}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowSin((prev) => !prev)}
                      title={showSin ? "Hide SIN" : "Show SIN"}
                    >
                      {showSin ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>}
              </div>
            </fieldset>
          )}
          {!isAdmin && (
            <fieldset className="border p-3 mb-4">
              <legend className="w-auto px-2">Referral & Source</legend>
              <div className="row mb-3">
                <div className="col-md-3">
                  <label>Referred by someone?</label>
                  <select
                    className="form-select"
                    name="refer_status"
                    value={form.refer_status ?? ""}
                    onChange={handleChange}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>

                {form.refer_status === "Yes" ? (
                  <>
                    <div className="col-md-3">
                      <label>
                        Referred By <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="refer_by_name"
                        className="form-control"
                        placeholder="Referred by who?"
                        value={form.refer_by_name ?? ""}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label>Employee ID</label>
                      <input
                        type="text"
                        name="refer_by_id"
                        className="form-control"
                        placeholder="Employee ID"
                        value={form.refer_by_id ?? ""}
                        onChange={handleChange}
                      />
                    </div>
                  </>
                ) : (
                  <div className="col-md-6" />
                )}
              </div>

              {isCandidate && (
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label>
                      How did you hear about us?{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      name="hear_about_us"
                      className="form-select"
                      value={form.hear_about_us || ""}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select</option>
                      {hearAboutList.map((item) => (
                        <option key={item.hau_id} value={item.hau_id}>
                          {item.hau_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </fieldset>
          )}

          {!isAdmin && (
            <>
              {/* Address */}
              <fieldset className="border p-3 mb-4">
                <legend className="w-auto px-2">Current Address</legend>

                {isCandidate ? (
                  <AddressSection
                    form={form}
                    setForm={setForm}
                    countries={countries}
                  />
                ) : (
                  <>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label>
                          Address 1 <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          name="address1"
                          className="form-control"
                          value={form.address1 ?? ""}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label>Address 2</label>
                        <input
                          type="text"
                          name="address2"
                          className="form-control"
                          value={form.address2 ?? ""}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <AddressSection
                      form={form}
                      setForm={setForm}
                      countries={countries}
                    />
                  </>
                )}
              </fieldset>

              {/* Employement */}
              <fieldset className="border p-3 mb-4">
                <legend className="w-auto px-2">Employement</legend>
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label>
                      Employment Category <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="emp_category"
                      value={form.emp_category ?? ""}
                      onChange={handleChange}
                      required
                      disabled
                    >
                      <option value="">Select</option>
                      {categories.map((cat) => (
                        <option key={cat.c_id} value={cat.c_id}>
                          {cat.c_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label>Cross-Border (DOT)</label>
                    <div className="btn-group w-100" role="group">
                      <input
                        type="radio"
                        className="btn-check"
                        name="cross_border_dot"
                        id="cross_dot_yes"
                        value="1"
                        checked={
                          form.cross_border_dot === "1" ||
                          form.cross_border_dot === 1
                        }
                        onChange={handleChange}
                      />
                      <label
                        className="btn btn-outline-primary"
                        htmlFor="cross_dot_yes"
                      >
                        Yes
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="cross_border_dot"
                        id="cross_dot_no"
                        value="0"
                        checked={
                          form.cross_border_dot === "0" ||
                          form.cross_border_dot === 0
                        }
                        onChange={handleChange}
                      />
                      <label
                        className="btn btn-outline-secondary"
                        htmlFor="cross_dot_no"
                      >
                        No
                      </label>
                    </div>
                  </div>
                </div>
              </fieldset>

              {/* Assign To */}
              <fieldset className="border p-3 mb-4">
                <legend className="w-auto px-2">Assign To</legend>

                <div className="row mb-3">
                  {/* 💡 Org Unit removed from UI; we auto-derive from position */}

                  <div className="col-md-5">
                    <label>
                      Position <span className="text-danger">*</span>
                    </label>
                    <Select
                      name="position_full"
                      value={
                        positionOptions.find(
                          (opt) =>
                            String(opt.value) === String(form.position_full),
                        ) || null
                      }
                      onChange={(selected) =>
                        setForm((prev) => ({
                          ...prev,
                          position_full: selected?.value || "", // now p_id
                        }))
                      }
                      options={positionOptions}
                      placeholder="Select position"
                      isSearchable
                      getOptionLabel={(e) => e.displayLabel}
                      formatOptionLabel={(data, { context }) => {
                        if (context === "menu") {
                          return (
                            <div>
                              <div className="react-select-label">
                                {data.displayLabel}
                              </div>
                              <div className="react-select-subtext">
                                {data.subtext}
                              </div>
                            </div>
                          );
                        }
                        return `${data.title} - ${data.orgName} - ${data.typeName}`;
                      }}
                      required
                      isDisabled
                    />
                  </div>
                </div>

                {/* Candidate-only extras */}
                {isCandidate && (
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <label>Group</label>
                      <select
                        name="candidate_group"
                        className="form-select"
                        value={form.candidate_group || ""}
                        onChange={handleChange}
                      >
                        <option value="">Select</option>
                        {canGroups.map((g) => (
                          <option key={g.pg_id} value={g.pg_id}>
                            {g.pg_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-3">
                      <label>Recruiter</label>
                      <select
                        name="emp_recruiter"
                        className="form-select"
                        value={form.emp_recruiter || ""}
                        onChange={handleChange}
                      >
                        <option value="">Select</option>
                        {recruiterList.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </fieldset>
            </>
          )}

          {/* Account Info — hidden for Candidates, shown for others */}
          <fieldset className="border p-3 mb-4 account-security-fieldset">
            <legend className="w-auto px-2">Account & Security</legend>
              <div className="row mb-3">
                <div className="col-md-5">
                  <label>
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    value={form.email ?? ""}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-5">
                  <label>Password Reset</label>
                  <div className="account-password-field">
                    <input
                      type={passwordVisible ? "text" : "password"}
                      name="password"
                      className="form-control"
                      value={form.password || ""}
                      onChange={handleChange}
                      placeholder="Leave blank to keep current password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="account-password-toggle"
                      onClick={() => setPasswordVisible(!passwordVisible)}
                      aria-label={passwordVisible ? "Hide password" : "Show password"}
                      title={passwordVisible ? "Hide password" : "Show password"}
                      data-no-standard-button="true"
                    >
                      {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-5">
                  <label>Confirm Password</label>
                  <input
                    type={passwordVisible ? "text" : "password"}
                    className="form-control"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Leave blank to keep current password"
                    autoComplete="new-password"
                  />
                </div>
              </div>
              <div className=" mb-3">
                <label>Profile Picture</label>
                {previewUrl && (
                  <div className="mb-2">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      width="100"
                      height="100"
                      style={{ objectFit: "cover", borderRadius: "8px" }}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="form-control"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file && file instanceof Blob) {
                      setProfilePic(file);
                      setPreviewUrl(URL.createObjectURL(file));
                    } else {
                      setProfilePic(null);
                      setPreviewUrl(null);
                      alert("Please select a valid image file.");
                    }
                  }}
                />
              </div>
          </fieldset>

          {/* Admin-only: roles/groups */}
          {isAdmin && (
            <fieldset className="border p-3 mb-4">
              <legend className="w-auto pb-3">
                Role, Group and Org Units Assignment
              </legend>
              <div className="row">
                <div className="col-md-4">
                  <label>
                    <strong>Roles</strong>
                  </label>
                  <div className="form-check">
                    {empRoles.map((role) => {
                      const isSuperAdminRole = role.r_name === "Super Admin";
                      return (
                        <div key={role.r_id}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`role-${role.r_id}`}
                            checked={selectedRoles.includes(Number(role.r_id))}
                            onChange={() =>
                              toggleSelection(
                                Number(role.r_id),
                                selectedRoles,
                                setSelectedRoles,
                              )
                            }
                            disabled={
                              (isSuperAdminRole && !loggedInUserIsSuperAdmin) ||
                              !canAssign
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`role-${role.r_id}`}
                          >
                            {role.r_name}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="col-md-4">
                  <label>
                    <strong>Groups</strong>
                  </label>
                  <div className="form-check">
                    {empGroups.map((group) => (
                      <div key={group.pg_id}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`group-${group.pg_id}`}
                          checked={selectedGroups.includes(Number(group.pg_id))}
                          onChange={() =>
                            toggleSelection(
                              Number(group.pg_id),
                              selectedGroups,
                              setSelectedGroups,
                            )
                          }
                          disabled={!canAssign}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`group-${group.pg_id}`}
                        >
                          {group.pg_name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="col-md-4">
                  <label>
                    <strong>Organizational Units</strong>
                  </label>
                  <div className="form-check">
                    {empOrgs.map((org) => (
                      <div key={org.ou_id}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`org-${org.ou_id}`}
                          checked={selectedOrgs.includes(org.ou_id)}
                          onChange={() =>
                            toggleSelection(
                              org.ou_id,
                              selectedOrgs,
                              setSelectedOrgs,
                            )
                          }
                          disabled={!canAssign}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`org-${org.ou_id}`}
                        >
                          {org.ou_name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </fieldset>
          )}

          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary px-5"
              disabled={isSubmitting}
            >
              Save
            </button>
          </div>
          </fieldset>
        </form>

        {userId && !isAdminUserManagement && (
          <>
            <SignatureManager
              taskName="user_information"
              taskId={PERSONAL_INFORMATION_TASK_ID}
              userId={isAdminRole && paramId ? paramId : userId}
              isAdmin={isAdminRole}
            />

            <ReviewManager
              taskName="user_information"
              taskId={PERSONAL_INFORMATION_TASK_ID}
              userId={isAdminRole && paramId ? paramId : userId}
              isAdmin={isAdminRole}
            />
          </>
        )}
      </div>
    </>
  );
}

export default EditUserAdmin;
